import { ViewType } from 'common/constants';
import { urlWithTenant } from 'common/utils/utilFunctions';

import {
	AIRecipesData,
	IAIRecipesResponse,
	IClickEditbtn,
	IGetAISharedSimulation,
} from 'features/AiSimulation/api/type-def';
import {
	useEffect,
	useState,
	useDispatch,
	useSelector,
	useNavigate,
	RootState,
	setAIRecipeDetail,
	setToastMessage,
	getAiRecipePath,
	getAISimulationRecipesData,
	Loader,
	HTTP_STATUS_CODES,
	APP_GENERIC_BAD_REQUEST_MSG,
	APP_GENERIC_DATA_NOT_FOUND_MSG,
	APP_GENERAL_ERROR_MSG,
	Grid,
	Chip,
	Stack,
	AICard,
	AITable,
	toSentenceCase,
	AI_TYPE,
	AlertComponent,
	useCallback,
	styles,
	useTableQuery,
	onClickEditHandler,
	onClickDeleteHandler,
	AIRecipesDataResponse,
	IAIRecipeDetails,
	noDataElement,
	lastUpdatedElement,
} from 'features/AiSimulation/SimulationArena/SimulationArenaModules';

const { RECIPE } = AI_TYPE;
const { OK, BAD_REQUEST, NOT_FOUND } = HTTP_STATUS_CODES;

const SimulationRecipes = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const simulationViewType = useSelector(
		(state: RootState) => state.simulationArena.simulationViewTypeValue,
	);

	const [viewType, setViewType] = useState<ViewType>(simulationViewType);

	const [isRecipesLoading, setIsRecipesLoading] = useState<boolean>(false);
	const [aiRecipeApiData, setAIRecipeApiData] =
		useState<AIRecipesDataResponse>();
	const [aiRecipesTotalRecordsCount, setAIRecipesTotalRecordCount] =
		useState<number>(0);

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);
	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);

	const displayToast = (message: string, severity: string) => {
		dispatch(
			setToastMessage({
				message,
				severity,
				isToastOpen: true,
			}),
		);
	};

	const defaulRecipeQuery = {
		email: loggedInUser?.email,
		type: RECIPE,
	};

	const defaultPaginationParams = {
		pageNumber: 1,
		pageSize: 10,
	};

	const { tableQueryObj: recipeTableQuery } = useTableQuery({
		id: 'simulationtableId_0',
	});

	useEffect(() => {
		setViewType(simulationViewType);
	}, [simulationViewType]);

	useEffect(() => {
		const { storeTableQuery } = {
			storeTableQuery: recipeTableQuery,
		};

		const queryObjFormation = {
			page: storeTableQuery.pageNumber + 1,
			size: storeTableQuery.pageSize,
		};

		fetchAIRecipesResponse(
			simulationViewType === ViewType.GRID
				? defaulRecipeQuery
				: { ...defaulRecipeQuery, ...queryObjFormation },
			false,
		);
	}, [simulationViewType]);

	const handleRecipePagination = (data: IGetAISharedSimulation) => {
		const defaultQuery: IAIRecipesResponse = {
			email: loggedInUser?.email || '',
			type: RECIPE,
			page: data.pageNumber + 1,
			size: data.pageSize,
		};
		fetchAIRecipesResponse(defaultQuery, false);
	};

	const fetchAIRecipesResponse = async (
		data: IAIRecipesResponse,
		navigateFlag: boolean,
	) => {
		setIsRecipesLoading(true);

		const responseFromApi = await getAISimulationRecipesData(data);

		if (responseFromApi?.responseBody && responseFromApi?.statusCode === OK) {
			if (navigateFlag) {
				const recipeData = responseFromApi?.responseBody
					?.data[0] as IAIRecipeDetails;
				dispatch(
					setAIRecipeDetail({
						...recipeData,
						isShared: true,
					}),
				);
				navigate(urlWithTenant(getAiRecipePath(recipeData.models)));
			} else {
				setAIRecipeApiData(responseFromApi?.responseBody);
				setAIRecipesTotalRecordCount(responseFromApi?.responseBody?.total);
				setIsRecipesLoading(false);
			}
		} else {
			if (responseFromApi?.statusCode === BAD_REQUEST) {
				displayToast(APP_GENERIC_BAD_REQUEST_MSG, 'error');
			} else if (responseFromApi?.statusCode === NOT_FOUND) {
				displayToast(APP_GENERIC_DATA_NOT_FOUND_MSG, 'error');
			} else {
				displayToast(APP_GENERAL_ERROR_MSG, 'error');
			}
			setIsRecipesLoading(false);
		}
	};

	const clickHandler = (rowData: IClickEditbtn, type: string) => {
		onClickEditHandler(rowData, type, dispatch, navigate);
	};

	const completionHandler = (
		isAPICallSuccess: boolean,
		errorMessage: string,
	) => {
		if (isAPICallSuccess) {
			fetchAIRecipesResponse(
				{ ...defaulRecipeQuery, ...defaultPaginationParams },
				false,
			);
			setViewType(ViewType.TABLE);
		} else if (errorMessage) {
			displayToast(errorMessage, 'error');
		}
	};

	const deleteHandler = (rowData: IClickEditbtn, type: string) => {
		onClickDeleteHandler(rowData, type, completionHandler);
	};
	return (
		<div>
			{viewType === ViewType.GRID ? (
				<Grid
					className={`${styles['section-content']} ${styles['grid-style']}`}
					container
					spacing={3}
				>
					{isRecipesLoading && (
						<Grid item sm={12}>
							<Loader
								show={isRecipesLoading}
								loaderText="Loading..."
								className={styles['loader-alignment']}
							/>
						</Grid>
					)}
					{!isRecipesLoading &&
						(aiRecipeApiData?.data !== undefined &&
						aiRecipeApiData?.data?.length > 0
							? aiRecipeApiData?.data.map((res: AIRecipesData) => (
									<Grid
										item
										key={res.id}
										xs={4}
										sm={4}
										md={4}
										lg={4}
										xl={4}
										direction="row"
									>
										<AICard
											data={res}
											type={RECIPE}
											title={res.name}
											path={getAiRecipePath(res.models)}
											key={res.id}
										>
											<Stack
												direction="row"
												justifyContent="space-between"
												alignItems="center"
												className={styles['aicard-content']}
											>
												{res.models.length > 0 && (
													<Chip
														key={res.models[res.models.length - 1].workflowStep}
														label={
															res.models.length > 0 &&
															toSentenceCase(
																res.models[res.models.length - 1].workflowStep,
															)
														}
														className={styles['type-style']}
														variant="outlined"
													/>
												)}
												{lastUpdatedElement(res)}
											</Stack>
										</AICard>
									</Grid>
							  ))
							: noDataElement())}
				</Grid>
			) : (
				<AITable
					data={aiRecipeApiData?.data}
					totalItems={aiRecipesTotalRecordsCount}
					onClickEditHandler={clickHandler}
					onClickDeleteHandler={deleteHandler}
					handlePagination={handleRecipePagination}
					isLoading={isRecipesLoading}
					type={RECIPE}
				/>
			)}
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
				data-testid="alert-component"
			/>
		</div>
	);
};

export default SimulationRecipes;
